/**
 * Amplify Gen1 호환 레이어
 * v6의 모듈화된 API를 Gen1 스타일로 래핑하여 기존 코드와의 호환성 유지
 */

import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  type SignInInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from "aws-amplify/auth";
import { fetchAuthSession } from "aws-amplify/auth";
import { get, post, del, patch, head, put } from "aws-amplify/api";
import {
  list as storageList,
  getUrl,
  remove as storageRemove,
  uploadData as storageUploadData,
  getProperties as storageGetProperties,
} from "aws-amplify/storage";
import { decodeJwtPayload } from "./jwt";
// 검수 모드는 백엔드 요청을 만들 수 있는 모듈보다 먼저 서야 한다 — 그런 모듈은 모두 이 헬퍼를 거친다(`reviewModeHeader`).
import "./reviewModeHeader";

/**
 * Auth 호환 레이어 - Gen1 스타일 유지
 *
 * 가입(`signUp`·`confirmSignUp`·`resendSignUp`)은 여기 없다 — 웹은 IdP 가입 API 를 직접 부르지 않고 백엔드 가입 API 를 쓴다
 * (`src/domains/shared/api/signup/`, 웹 스펙 `signup`).
 */
export const Auth = {
  signIn: async (username: string, password: string) => {
    const result = await amplifySignIn({ username, password } as SignInInput);
    if (result.isSignedIn) {
      const attributes = await fetchUserAttributes();
      return {
        ...result,
        attributes,
        username: result.nextStep?.signInStep === "DONE" ? username : undefined,
      };
    }
    // Amplify v6는 미인증 사용자에 대해 throw 대신 nextStep으로 반환함.
    // Gen1 호환 유지를 위해 기존 코드가 기대하는 에러 형태로 변환한다.
    if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
      throw { name: "UserNotConfirmedException" };
    }
    return result;
  },
  /** 이 기기에서만 로그아웃한다 — 전역 로그아웃은 다른 앱의 로그인까지 끝내므로 받지 않는다(exclusive-login). */
  signOut: async () => {
    await amplifySignOut();
  },
  currentAuthenticatedUser: async () => {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    return { username: user.username, attributes };
  },
  /** 토큰을 강제로 갱신한다 — 서버가 바꾼 그룹(가입 마무리)이 보호 화면의 게이트 전에 토큰에 실리게 한다. */
  refreshSession: async () => {
    await fetchAuthSession({ forceRefresh: true });
  },
  currentSession: async () => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const accessToken = session.tokens?.accessToken;
    return {
      getIdToken: () => ({
        getJwtToken: () => idToken?.toString() ?? "",
        payload: decodeJwtPayload(idToken),
      }),
      getAccessToken: () => ({
        getJwtToken: () => accessToken?.toString() ?? "",
        payload: decodeJwtPayload(accessToken),
      }),
      getRefreshToken: () => ({
        getToken: () => (session.tokens as any)?.refreshToken?.toString() ?? "",
      }),
    };
  },
  currentUserInfo: async () => {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      return { username: user.username, attributes };
    } catch {
      return null;
    }
  },
  forgotPassword: async (username: string) => {
    return await resetPassword({ username } as ResetPasswordInput);
  },
  forgotPasswordSubmit: async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    } as ConfirmResetPasswordInput);
    return "SUCCESS";
  },
};

const getStatusCode = (httpResponse: any): number | undefined =>
  httpResponse?.statusCode || httpResponse?.status;

const handleHttpResponse = async (httpResponse: any): Promise<any> => {
  const statusCode = getStatusCode(httpResponse);
  if (statusCode === 204) return { status: "Success", statusCode: 204 };
  try {
    return await httpResponse.body.json();
  } catch (error) {
    if (statusCode !== undefined && statusCode >= 200 && statusCode < 300)
      return { status: "Success", statusCode };
    throw error;
  }
};

const createHeadersWithAuth = async (
  existingHeaders?: Record<string, string>
): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    return {
      ...existingHeaders,
      Authorization: session.tokens?.idToken?.toString() ?? "",
    };
  } catch {
    return existingHeaders || {};
  }
};

/**
 * API 호환 레이어 - Gen1 스타일 유지
 *
 * 모든 REST 트래픽은 통합 게이트웨이(`DronesquareApi-V1`)로 향한다. 경로 선행 슬래시는
 * endpoint base 와의 `//` 중복을 막기 위해 각 메서드에서 경량 정규화한다(미래 호출부 안전망).
 */
const normalizePath = (path: string): string => path.replace(/^\//, "");

export const API = {
  get: async (apiName: string, path: string, init?: any) => {
    const response = get({
      apiName,
      path: normalizePath(path),
      options: { headers: await createHeadersWithAuth(init?.headers) },
    });
    return await handleHttpResponse(await response.response);
  },
  post: async (apiName: string, path: string, init?: any) => {
    const response = post({
      apiName,
      path: normalizePath(path),
      options: {
        body: init?.body,
        headers: await createHeadersWithAuth(init?.headers),
      },
    });
    return await handleHttpResponse(await response.response);
  },
  patch: async (apiName: string, path: string, init?: any) => {
    const response = patch({
      apiName,
      path: normalizePath(path),
      options: {
        body: init?.body,
        headers: await createHeadersWithAuth(init?.headers),
      },
    });
    return await handleHttpResponse(await response.response);
  },
  del: async (apiName: string, path: string, init?: any) => {
    const response = del({
      apiName,
      path: normalizePath(path),
      options: {
        body: init?.body,
        headers: await createHeadersWithAuth(init?.headers),
      },
    });
    return await handleHttpResponse(await response.response);
  },
  head: async (apiName: string, path: string, init?: any) => {
    const response = head({
      apiName,
      path: normalizePath(path),
      options: { headers: await createHeadersWithAuth(init?.headers) },
    });
    return await response.response;
  },
  put: async (apiName: string, path: string, init?: any) => {
    const response = put({
      apiName,
      path: normalizePath(path),
      options: {
        body: init?.body,
        headers: await createHeadersWithAuth(init?.headers),
      },
    });
    return await handleHttpResponse(await response.response);
  },
};

/**
 * Storage 호환 레이어 - Gen1 스타일 유지
 */
export const Storage = {
  list: async (
    path: string,
    options?: any,
    addPrefixPublicToPath = true,
    addPrefixPublicToOutput = false
  ) => {
    let modifiedPath = path.startsWith("public/")
      ? path
      : addPrefixPublicToPath
      ? `public/${path}`
      : path;
    const toOutputKey = (key: string) =>
      key.replace("public/", addPrefixPublicToOutput ? "public/" : "");
    const result = await storageList({
      path: modifiedPath,
      options: {
        pageSize: options?.pageSize,
        nextToken: options?.nextToken,
        subpathStrategy: options?.subpathStrategy,
      } as any,
    });
    if ("items" in result) {
      return {
        results: result.items.map((item: any) => ({
          key: toOutputKey(item.path || item.key),
          size: item.size,
          lastModified: item.lastModified,
          eTag: item.eTag,
        })),
        excludedSubpaths: ((result as any).excludedSubpaths ?? []).map(
          toOutputKey
        ) as string[],
        hasMore: !!result.nextToken,
        nextToken: result.nextToken,
      };
    }
    return {
      results: [] as any[],
      excludedSubpaths: [] as string[],
      hasMore: false,
    };
  },
  get: async (
    key: string,
    options?: Record<string, unknown>
  ): Promise<string> => {
    const { url } = await getUrl({
      path: key.startsWith("public/") ? key : `public/${key}`,
    });
    return url.toString();
  },
  put: async (key: string, file: any, config?: any) => {
    const path = key.startsWith("public/") ? key : `public/${key}`;
    try {
      const uploadOperation = storageUploadData({
        path,
        data: file,
        options: {
          contentType: config?.contentType,
          metadata: config?.metadata,
          onProgress: config?.progressCallback,
        },
      });
      const result = await uploadOperation.result;
      return { key: result.path.replace("public/", "") };
    } catch (error) {
      console.error("[Storage.put failed]", path, error);
      throw error;
    }
  },
  remove: async (key: string) => {
    await storageRemove({
      path: key.startsWith("public/") ? key : `public/${key}`,
    });
  },
  getProperties: async (key: string) => {
    return await storageGetProperties({
      path: key.startsWith("public/") ? key : `public/${key}`,
    });
  },
};

/**
 * main-api 직접 호출(SSE 포함)용 Bearer 토큰 헬퍼.
 * Amplify gateway는 Bearer 없이 토큰만 받지만, main-api는 "Bearer <token>" 형식 필요.
 * SSE 재연결마다 새로 호출해야 JWT 만료를 방지할 수 있다.
 */
export const getIdToken = async (): Promise<string> => {
  const session = await fetchAuthSession();
  return `Bearer ${session.tokens?.idToken?.toString() ?? ""}`;
};
